import React from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { X, HelpCircle } from 'lucide-react';
import { useAuth } from '../auth/AuthProvider';

/** Full-page help + "?" button to open it. The page collects:
 *
 *  1. Reference content for the same surfaces the first-time cards
 *     introduce (in case the user dismissed the cards too quickly).
 *  2. A "re-enable first-time tips" action that clears the user's
 *     ``seen`` list and ``suppress_all`` flag via the auth context.
 *
 *  Mount this once at the App level; it portals its own button and the
 *  full-page overlay to the document body so it doesn't need anchor refs.
 *  (Was a right-side drawer; grown into a full page as the content grew.) */

const HELP_BUTTON_LABEL = 'Help';

/* ── Trigger button ────────────────────────────────────────── */

const TriggerButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    border: none;
    background: transparent;
    color: #777;
    cursor: pointer;

    &:hover {
        background: #f0f0f0;
        color: #333;
    }
`;

/* ── Full-page overlay ─────────────────────────────────────── */

// The whole page. A fixed overlay above everything, fading in; it covers the
// viewport and scrolls internally rather than sliding in from the edge.
const Overlay = styled.div<{ $open: boolean }>`
    position: fixed;
    inset: 0;
    background: #f8fafc;
    z-index: 1200;
    display: flex;
    flex-direction: column;
    opacity: ${({ $open }) => ($open ? 1 : 0)};
    pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
    transition: opacity 0.18s ease;
`;

const PageHeader = styled.header`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 16px 24px;
    background: #ffffff;
    border-bottom: 1px solid #e2e8f0;
    flex: 0 0 auto;
`;

const PageTitle = styled.h1`
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    color: #0f172a;
`;

const CloseButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 32px;
    padding: 0 12px;
    border-radius: 6px;
    border: 1px solid #e2e8f0;
    background: #ffffff;
    color: #475569;
    font-size: 13px;
    font-family: inherit;
    cursor: pointer;

    &:hover { color: #0f172a; background: #f1f5f9; }
`;

// Scroll region: a centered, readable-width container that lays the sections
// out in a responsive multi-column grid so the full page width earns its keep.
const PageBody = styled.div`
    flex: 1 1 auto;
    overflow-y: auto;
    padding: 28px 24px 40px;
`;

const ContentGrid = styled.div`
    max-width: 1000px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
    gap: 24px 32px;
    align-items: start;
`;

const Section = styled.section`
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    padding: 18px 20px;
`;

const SectionTitle = styled.h3`
    margin: 0 0 6px;
    font-size: 13px;
    font-weight: 600;
    color: #0f172a;
`;

const SectionBody = styled.div`
    font-size: 13px;
    line-height: 1.5;
    color: #334155;

    p { margin: 0 0 6px; }
    p:last-child { margin-bottom: 0; }
    ul { margin: 4px 0 0; padding-left: 18px; }
    li { margin-bottom: 4px; }
    strong { color: #0f172a; }
`;

const Footer = styled.div`
    max-width: 1000px;
    margin: 28px auto 0;
    padding-top: 20px;
    border-top: 1px solid #e2e8f0;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
`;

const ResetButton = styled.button`
    background: none;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 8px 12px;
    font-size: 13px;
    color: #334155;
    cursor: pointer;
    font-family: inherit;

    &:hover { background: #f8fafc; }
    &:disabled { color: #94a3b8; cursor: default; background: #f1f5f9; }
`;

const ResetHint = styled.span`
    font-size: 11px;
    color: #94a3b8;
`;

/* ── Component ────────────────────────────────────────────── */

export const HelpButton: React.FC = () => {
    const [open, setOpen] = React.useState(false);
    const { prefs, resetTutorial } = useAuth();
    const [justReset, setJustReset] = React.useState(false);

    // Close on Escape so the drawer behaves like a modal escape hatch.
    React.useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open]);

    // The "just reset" confirmation clears itself if the user dismisses
    // the drawer; re-opening starts a fresh interaction.
    React.useEffect(() => {
        if (!open) setJustReset(false);
    }, [open]);

    const handleReset = async () => {
        await resetTutorial();
        setJustReset(true);
    };

    const tipsAreSuppressed = prefs.suppress_all || prefs.seen.length > 0;

    return (
        <>
            <TriggerButton
                type="button"
                aria-label={HELP_BUTTON_LABEL}
                title={HELP_BUTTON_LABEL}
                onClick={() => setOpen(true)}
            >
                <HelpCircle size={16} />
            </TriggerButton>
            {createPortal(
                <Overlay $open={open} role="dialog" aria-modal="true" aria-label="Help" aria-hidden={!open}>
                    <PageHeader>
                        <PageTitle>Help</PageTitle>
                        <CloseButton
                            type="button"
                            aria-label="Close help"
                            onClick={() => setOpen(false)}
                        >
                            <X size={16} />
                            Close
                        </CloseButton>
                    </PageHeader>
                    <PageBody>
                        <ContentGrid>
                            <Section>
                                <SectionTitle>What this tool does</SectionTitle>
                                <SectionBody>
                                    <p>
                                        BigPDF splits a big PDF into the documents
                                        inside it, lets you fix the splits, search by
                                        full text, and export the result as separate
                                        PDFs. It fits into your intake workflow.
                                    </p>
                                </SectionBody>
                            </Section>

                            <Section>
                                <SectionTitle>Job queue</SectionTitle>
                                <SectionBody>
                                    <p>
                                        Anything you upload, re-analyze, or export
                                        shows up in the queue indicator in the top bar.
                                        Click it to see active jobs, queued ones, and
                                        recent results. Cancel from the same panel.
                                    </p>
                                </SectionBody>
                            </Section>

                            <Section>
                                <SectionTitle>Cases</SectionTitle>
                                <SectionBody>
                                    <p>
                                        A case is a collection of documents from one
                                        or more uploaded PDFs. From the case view you
                                        can search, sort, and filter the document list
                                        (including by <strong>category</strong>), and
                                        merge documents that were split apart by
                                        mistake.
                                    </p>
                                    <p>
                                        <strong>Search</strong> looks at titles,
                                        categories, and the full page text — documents
                                        matching by title or category surface first.
                                    </p>
                                    <p>
                                        The optional <strong>Uploaded</strong> column
                                        (in the column menu) shows when each document was
                                        added to this tool — the same for a manual upload
                                        or a Filevine pull. The <strong>Source file</strong>
                                        {' '}column shows the original filename it came
                                        from.
                                    </p>
                                    <p>
                                        Titles, dates, and categories are inferred by
                                        the AI and aren&apos;t 100% accurate — spot-check
                                        anything important.
                                    </p>
                                </SectionBody>
                            </Section>

                            <Section>
                                <SectionTitle>Categories</SectionTitle>
                                <SectionBody>
                                    <p>
                                        Each document gets a <strong>category</strong>
                                        {' '}— the AI&apos;s best guess at where it
                                        belongs on the <strong>intake checklist</strong>.
                                        Categories map directly to the checklist&apos;s
                                        sections (and their more specific leaves), so a
                                        categorized case lines up with the checklist. The
                                        category shows as a capsule on the document and
                                        in the list.
                                    </p>
                                    <ul>
                                        <li>
                                            <strong>Change</strong> the category if the
                                            guess is wrong, or <strong>Confirm</strong>
                                            {' '}it when it&apos;s already right.
                                        </li>
                                        <li>
                                            A category you change or confirm is treated
                                            as correct and <strong>survives
                                            re-analysis</strong> — it won&apos;t be
                                            overwritten.
                                        </li>
                                        <li>
                                            <strong>&ldquo;Needs review — not
                                            classified&rdquo;</strong> means auto-classification
                                            couldn&apos;t place that document. Assign a
                                            category manually.
                                        </li>
                                    </ul>
                                    <p>
                                        Categories are less reliable than titles and
                                        dates — confirm the ones that matter.
                                    </p>
                                    <p>
                                        <strong>Confirming a category is not the same as
                                        reviewing a document.</strong> Confirm settles
                                        what the document <em>is</em> (its checklist
                                        category); marking it <strong>Reviewed</strong>
                                        {' '}settles that its <em>boundaries</em> are
                                        right — that the pages were split into the
                                        correct document. A document can be reviewed but
                                        not category-confirmed, or the reverse.
                                    </p>
                                </SectionBody>
                            </Section>

                            <Section>
                                <SectionTitle>Inspecting a document</SectionTitle>
                                <SectionBody>
                                    <ul>
                                        <li><strong>Split</strong> a document if it should be two.</li>
                                        <li><strong>Merge</strong> with the previous or next document, from the toolbar&apos;s <strong>Actions</strong> menu.</li>
                                        <li>Jump to the <strong>previous / next document</strong> with the arrows in the toolbar. Previews of the neighboring documents sit at the ends of the page scroll (&ldquo;Show pages&rdquo;).</li>
                                        <li><strong>Search</strong> within the document&apos;s OCR text.</li>
                                        <li>Toggle <strong>Image / Text / Side-by-Side</strong> to compare the page and the extracted OCR — drag the divider between the panes (or beside the page rail) to resize them.</li>
                                        <li><strong>Change</strong> or <strong>Confirm</strong> the document&apos;s category (see Categories above).</li>
                                        <li>Mark a document <strong>Reviewed</strong> when its boundaries are correct — that confirms the split for the team and helps train the model. (Separate from confirming the category.)</li>
                                    </ul>
                                </SectionBody>
                            </Section>

                            <Section>
                                <SectionTitle>Filevine</SectionTitle>
                                <SectionBody>
                                    <p>
                                        A case can be <strong>linked</strong> to a
                                        Filevine project (from the case list). Linked
                                        cases get two extra actions in the case header:
                                    </p>
                                    <ul>
                                        <li>
                                            <strong>Pull from Filevine</strong> — pick a
                                            document from the project and it&apos;s
                                            downloaded, split into documents, and
                                            analyzed into this case. One document per
                                            pull; nothing is imported automatically.
                                            Documents already pulled into the case are
                                            flagged in the picker so you don&apos;t pull
                                            them twice.
                                        </li>
                                        <li>
                                            <strong>Push to Filevine</strong> — after
                                            review, sends the split documents back to
                                            the project as individual PDFs plus an index,
                                            in a new &ldquo;pdf_processing_output&rdquo;
                                            folder. Filenames get the document date
                                            prepended so they sort chronologically. The
                                            push only <em>adds</em> files — the original
                                            document in Filevine is never modified.
                                        </li>
                                    </ul>
                                    <p>
                                        Each push lands in its own dated subfolder, so
                                        it&apos;s safe to push again after corrections —
                                        nothing is overwritten.
                                    </p>
                                </SectionBody>
                            </Section>
                        </ContentGrid>
                        <Footer>
                            <ResetButton
                                type="button"
                                onClick={() => { void handleReset(); }}
                                disabled={!tipsAreSuppressed || justReset}
                            >
                                {justReset ? 'Tips re-enabled.' : 'Re-enable first-time tips'}
                            </ResetButton>
                            {!tipsAreSuppressed && !justReset && (
                                <ResetHint>No tips have been dismissed yet.</ResetHint>
                            )}
                        </Footer>
                    </PageBody>
                </Overlay>,
                document.body,
            )}
        </>
    );
};

export default HelpButton;
